/**
 * @file LinksSubTab Component
 * @description Sub-tab for managing investment hyperlinks within the Related tab.
 * Provides a table listing of links with add, edit, and delete operations.
 */

import {
  useLinks,
  useCreateLink,
  useUpdateLink,
  useDeleteLink,
} from '@medsuite/api-client';
import { Button, Card, Spinner } from '@medsuite/ui-components';
import { useState, useCallback } from 'react';

import { LinkModal } from './LinkModal';

import type { InvestmentLink, Investment } from '@medsuite/models';

interface LinksSubTabProps {
  investment: Investment;
}

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Delete confirmation modal for links
 */
function DeleteLinkModal({
  link,
  onConfirm,
  onClose,
  isDeleting,
}: {
  link: InvestmentLink;
  onConfirm: () => void;
  onClose: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Delete Link
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Are you sure you want to delete &ldquo;{link.title}&rdquo;? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * LinksSubTab Component
 */
export function LinksSubTab({ investment }: LinksSubTabProps) {
  const investmentId = investment.investment_id;

  // State
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLink, setEditingLink] = useState<InvestmentLink | null>(null);
  const [deletingLink, setDeletingLink] = useState<InvestmentLink | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

  // Queries and mutations
  const { data: links = [], isLoading } = useLinks(investmentId);
  const createLink = useCreateLink(investmentId);
  const updateLink = useUpdateLink(investmentId);
  const deleteLink = useDeleteLink(investmentId);

  // Handle create
  const handleCreate = useCallback(
    async (title: string, url: string) => {
      try {
        const result = await createLink.mutateAsync({ title, url });
        if (result.duplicate_warning) {
          setDuplicateWarning(result.duplicate_warning);
        } else {
          setShowAddModal(false);
          setDuplicateWarning(null);
        }
      } catch (error) {
        console.error('Failed to create link:', error);
      }
    },
    [createLink]
  );

  // Handle update
  const handleUpdate = useCallback(
    async (title: string, url: string) => {
      if (!editingLink) {
        return;
      }

      try {
        const result = await updateLink.mutateAsync({
          linkId: editingLink.link_id,
          data: { title, url },
        });
        if (result.duplicate_warning) {
          setDuplicateWarning(result.duplicate_warning);
        } else {
          setEditingLink(null);
          setDuplicateWarning(null);
        }
      } catch (error) {
        console.error('Failed to update link:', error);
      }
    },
    [editingLink, updateLink]
  );

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!deletingLink) {
      return;
    }

    try {
      await deleteLink.mutateAsync(deletingLink.link_id);
      setDeletingLink(null);
    } catch (error) {
      console.error('Failed to delete link:', error);
    }
  }, [deletingLink, deleteLink]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Links
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Add hyperlinks to external references, documentation, or related resources.
              </p>
            </div>
            <Button onClick={() => setShowAddModal(true)}>+ Add Link</Button>
          </div>

          {links.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <div className="text-4xl mb-2">&#128279;</div>
              <p>No links yet.</p>
              <p className="text-sm">Add links to external references or documentation.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      URL
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Modified
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {links.map((link) => (
                    <tr
                      key={link.link_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="px-4 py-4">
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {link.title}
                        </a>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400 break-all max-w-xs block">
                          {link.url.length > 60 ? `${link.url.substring(0, 60)}...` : link.url}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(link.created_at)}
                        {link.created_by && (
                          <div className="text-xs">{link.created_by}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(link.modified_at)}
                        {link.modified_by && (
                          <div className="text-xs">{link.modified_by}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingLink(link);
                              setDuplicateWarning(null);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setDeletingLink(link)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Add Link Modal */}
      {showAddModal && (
        <LinkModal
          onSave={handleCreate}
          onClose={() => {
            setShowAddModal(false);
            setDuplicateWarning(null);
          }}
          isSaving={createLink.isPending}
          duplicateWarning={duplicateWarning}
        />
      )}

      {/* Edit Link Modal */}
      {editingLink && (
        <LinkModal
          link={editingLink}
          onSave={handleUpdate}
          onClose={() => {
            setEditingLink(null);
            setDuplicateWarning(null);
          }}
          isSaving={updateLink.isPending}
          duplicateWarning={duplicateWarning}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingLink && (
        <DeleteLinkModal
          link={deletingLink}
          onConfirm={handleDelete}
          onClose={() => setDeletingLink(null)}
          isDeleting={deleteLink.isPending}
        />
      )}
    </div>
  );
}
